import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, CheckCircle2, Landmark, User, AlertCircle } from 'lucide-react';
import { useDataProvider, useNotify } from 'ra-core';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

type AdditionalContact = {
    name: string;
    designation: string;
    email: string;
    phone: string;
};

export const VendorOnboardingPage = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [inviteData, setInviteData] = useState<{ email: string } | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [additionalContacts, setAdditionalContacts] = useState<AdditionalContact[]>([]);
    
    const { register, handleSubmit, formState: { errors }, setValue } = useForm();

    const updateAdditionalContact = (index: number, field: keyof AdditionalContact, value: string) => {
        setAdditionalContacts((contacts) =>
            contacts.map((contact, i) =>
                i === index ? { ...contact, [field]: value } : contact
            )
        );
    };

    const addAdditionalContact = () => {
        setAdditionalContacts((contacts) => [
            ...contacts,
            { name: '', designation: '', email: '', phone: '' },
        ]);
    };

    const removeAdditionalContact = (index: number) => {
        setAdditionalContacts((contacts) => contacts.filter((_, i) => i !== index));
    };

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setValidationError('No invitation token provided. Please use the link from your invitation email.');
                setIsValidating(false);
                return;
            }

            try {
                // Check if token exists and is valid
                const { data } = await dataProvider.getList('vendor_invites', {
                    filter: { token, used_at: null },
                    pagination: { page: 1, perPage: 1 },
                    sort: { field: 'created_at', order: 'DESC' },
                });

                if (data && data.length > 0) {
                    const invite = data[0];
                    // Check if expired
                    const expiresAt = new Date(invite.expires_at);
                    if (expiresAt < new Date()) {
                        setValidationError('This invitation has expired. Please contact the company for a new invitation.');
                    } else {
                        setIsValid(true);
                        setInviteData({ email: invite.email });
                        setValue('email', invite.email); // Pre-fill email
                    }
                } else {
                    setValidationError('Invalid or already used invitation token. Please contact the company for assistance.');
                }
            } catch (error) {
                console.error('Token validation error:', error);
                setValidationError('Error validating invitation. Please try again or contact support.');
            } finally {
                setIsValidating(false);
            }
        };

        validateToken();
    }, [token, dataProvider, setValue]);

    const onSubmit = async (data: any) => {
        if (!token || !isValid) return;
        
        setIsSubmitting(true);
        try {
            const contactPersons = additionalContacts
                .map((contact) => ({
                    name: contact.name.trim(),
                    designation: contact.designation.trim(),
                    email: contact.email.trim(),
                    phone: contact.phone.trim(),
                }))
                .filter((contact) =>
                    contact.name || contact.designation || contact.email || contact.phone
                );

            // Create vendor with pending_approval status
            const { data: vendor } = await dataProvider.create('vendors', {
                data: {
                    ...data,
                    status: 'pending_approval',
                    contact_persons: contactPersons,
                    // Map bank fields correctly to database columns
                    bank_account_number: data.bank_account_number,
                    bank_ifsc_code: data.bank_ifsc_code,
                }
            });

            // Mark invite as used
            await dataProvider.update('vendor_invites', {
                id: token,
                data: { 
                    used_at: new Date().toISOString(),
                    vendor_id: vendor.id 
                },
                previousData: {}
            });

            setSubmitted(true);
            notify('Application submitted successfully!', { type: 'success' });
        } catch (error) {
            console.error('Submission error:', error);
            notify('Error submitting application. Please try again.', { type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isValidating) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full text-center p-8">
                    <div className="flex justify-center mb-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                    <CardTitle className="text-xl font-semibold">Validating Invitation...</CardTitle>
                </Card>
            </div>
        );
    }

    if (validationError) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full text-center p-8 border-red-200">
                    <div className="flex justify-center mb-4">
                        <AlertCircle className="w-16 h-16 text-red-500" />
                    </div>
                    <CardTitle className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</CardTitle>
                    <CardDescription className="text-lg text-slate-600 mb-6">
                        {validationError}
                    </CardDescription>
                    <Button variant="outline" onClick={() => window.location.href = '/'}>
                        Back to Home
                    </Button>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full text-center p-8 bg-white border-green-200">
                    <div className="flex justify-center mb-6">
                        <CheckCircle2 className="w-16 h-16 text-green-500" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-slate-900 mb-2">Application Received!</CardTitle>
                    <CardDescription className="text-lg text-slate-600 mb-6">
                        Thank you for your interest in partnering with us. Your application is now under review. 
                        Our team will contact you shortly.
                    </CardDescription>
                    <Button variant="outline" onClick={() => window.location.href = '/'}>
                        Back to Home
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-10">
                    <img src="/logos/rovasys_horizontal.png" alt="Rovasys" className="h-12 mx-auto mb-4" />
                    <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Vendor Onboarding</h1>
                    <p className="mt-2 text-slate-600">Please provide your company details to apply as a registered vendor.</p>
                    {inviteData?.email && (
                        <p className="mt-2 text-sm text-slate-500">
                            Invitation sent to: <span className="font-medium">{inviteData.email}</span>
                        </p>
                    )}
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-8">
                        {/* Company Identification */}
                        <Section title="Company Identification" icon={<Building2 className="w-5 h-5" />}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormItem label="Vendor / Company Name" error={errors.name?.message as string}>
                                    <Input {...register('name', { required: 'Company name is required' })} placeholder="Legal Entity Name" />
                                </FormItem>
                                <FormItem label="Category" error={errors.category?.message as string}>
                                    <Input {...register('category')} placeholder="e.g. Raw Material, Services, Logistics" />
                                </FormItem>
                                <FormItem label="GSTIN" error={errors.gstin?.message as string}>
                                    <Input {...register('gstin')} placeholder="15-digit GST Number" />
                                </FormItem>
                                <FormItem label="PAN Number" error={errors.pan?.message as string}>
                                    <Input {...register('pan')} placeholder="10-digit PAN Number" />
                                </FormItem>
                            </div>
                        </Section>

                        {/* Contact Information */}
                        <Section title="Contact Information" icon={<User className="w-5 h-5" />}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormItem label="Contact Person" error={errors.contact_person?.message as string}>
                                    <Input {...register('contact_person', { required: 'Contact person is required' })} placeholder="Full Name" />
                                </FormItem>
                                <FormItem label="Designation">
                                    <Input {...register('designation')} placeholder="e.g. Sales Manager" />
                                </FormItem>
                                <FormItem label="Email Address" error={errors.email?.message as string}>
                                    <Input 
                                        {...register('email', { 
                                            required: 'Email is required',
                                            pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' }
                                        })} 
                                        type="email" 
                                        placeholder="email@company.com"
                                        defaultValue={inviteData?.email}
                                    />
                                </FormItem>
                                <FormItem label="Phone Number" error={errors.phone?.message as string}>
                                    <Input {...register('phone', { required: 'Phone is required' })} placeholder="+91 XXXXX XXXXX" />
                                </FormItem>
                                <div className="md:col-span-2 space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <Label className="text-sm font-semibold text-slate-700">
                                            Additional Contact Persons
                                        </Label>
                                        <Button type="button" variant="outline" size="sm" onClick={addAdditionalContact}>
                                            Add Contact
                                        </Button>
                                    </div>
                                    {additionalContacts.length > 0 && (
                                        <div className="space-y-3">
                                            {additionalContacts.map((contact, index) => (
                                                <div key={index} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <Input
                                                            value={contact.name}
                                                            onChange={(event) => updateAdditionalContact(index, 'name', event.target.value)}
                                                            placeholder="Contact name"
                                                        />
                                                        <Input
                                                            value={contact.designation}
                                                            onChange={(event) => updateAdditionalContact(index, 'designation', event.target.value)}
                                                            placeholder="Designation"
                                                        />
                                                        <Input
                                                            value={contact.email}
                                                            onChange={(event) => updateAdditionalContact(index, 'email', event.target.value)}
                                                            placeholder="Email"
                                                            type="email"
                                                        />
                                                        <Input
                                                            value={contact.phone}
                                                            onChange={(event) => updateAdditionalContact(index, 'phone', event.target.value)}
                                                            placeholder="Phone"
                                                        />
                                                    </div>
                                                    <div className="mt-3 flex justify-end">
                                                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAdditionalContact(index)}>
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <FormItem label="Registered Address" error={errors.address?.message as string}>
                                        <Textarea {...register('address', { required: 'Address is required' })} rows={3} placeholder="Full Registered Address" />
                                    </FormItem>
                                </div>
                            </div>
                        </Section>

                        {/* Bank Details */}
                        <Section title="Bank Details (For Payments)" icon={<Landmark className="w-5 h-5" />}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <FormItem label="Bank Name" className="md:col-span-1">
                                    <Input {...register('bank_name')} placeholder="e.g. HDFC Bank" />
                                </FormItem>
                                <FormItem label="Account Number" className="md:col-span-1">
                                    <Input {...register('bank_account_number')} placeholder="Account No." />
                                </FormItem>
                                <FormItem label="IFSC Code" className="md:col-span-1">
                                    <Input {...register('bank_ifsc_code')} placeholder="IFSC Code" />
                                </FormItem>
                            </div>
                        </Section>

                        <div className="flex items-center justify-end gap-4 mt-8 pb-12">
                            <Button type="button" variant="outline" onClick={() => window.history.back()}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSubmitting} className="min-w-[150px]">
                                {isSubmitting ? 'Submitting...' : 'Submit Application'}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Section = ({ title, icon, children }: { title: string, icon: React.ReactNode, children: React.ReactNode }) => (
    <Card className="border-slate-200">
        <CardHeader className="bg-slate-50 border-b pb-4">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-md border text-slate-600">
                    {icon}
                </div>
                <div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                </div>
            </div>
        </CardHeader>
        <CardContent className="pt-6">
            {children}
        </CardContent>
    </Card>
);

const FormItem = ({ label, error, children, className }: { label: string, error?: string, children: React.ReactNode, className?: string }) => (
    <div className={className}>
        <Label className="text-sm font-semibold text-slate-700 mb-2 block">{label}</Label>
        {children}
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
);
